//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, Button, Grid, TextField, Typography } from '@mui/material'
import React, { ReactElement, useEffect } from 'react'

import { EthAddr } from '@shared/SchemaTypes'

import { useOtDispatch, useOtSelector } from '@react/redux/store'

import { APR } from '@shared/conversions'
import { PoolOverviewCTO, PoolOverviewCTOSchema } from '@shared/sharedTypes'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import ViewField from '@react/components/elements/ViewField'
import { theme } from '@react/components/theme'
import { rolloverAndFinalizeAprTx } from '@react/signedTxs/rolloverAndFinalizeAprTx'

export default function BorrowerRolloverFinalizeForm(props: { poolAddr: EthAddr; initialAPR: APR }): ReactElement {
  const { poolAddr, initialAPR } = props
  const dispatch = useOtDispatch()

  const poolOverviewCTO = useOtSelector(
    (state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO] as PoolOverviewCTO
  )

  const [apr, setApr] = React.useState<number>(Number(initialAPR) / 100)
  const [errorAPR, setErrorAPR] = React.useState<string | null>()

  useEffect(() => {
    if (poolOverviewCTO?.requestedLoanState.loanAddr !== ETH_ADDR_ZERO) {
      setApr(Number(poolOverviewCTO.requestedLoanState.finalizedApr) / 100)
    }
  }, [poolOverviewCTO])

  const handleOnChangeAPR = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newApr = parseFloat(event.target.value)
    if (newApr < 0) {
      setErrorAPR('APR cannot be less than 0')
    } else {
      setErrorAPR(null)
    }
    setApr(newApr)
  }

  const [isSubmitted, setIsSubmitted] = React.useState(false)
  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    borderRadius: '6px',
  }

  const finalizeRolloverAction = (): void => {
    setIsSubmitted(true)
    dispatch(
      rolloverAndFinalizeAprTx({
        poolAddr,
        loanAddr: poolOverviewCTO.requestedLoanState.loanAddr,
        apr: Math.round(apr * 100).toString() as APR,
      })
    )
  }
  return (
    <Box
      data-testid={BorrowerRolloverFinalizeForm.name}
      px={0}
      py={0}
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        direction: 'column',
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant={'h6'}>Update the Collateral Sheet with the assets for this Pool</Typography>
        </Grid>

        <Grid item xs={12}>
          <ViewField
            dataObject={poolOverviewCTO}
            fieldSchema={PoolOverviewCTOSchema.describe().fields.rolloverOpeningLoanPrincipal}
          ></ViewField>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="bodyMuted">Finalize Loan APR</Typography>
        </Grid>
        <Grid item xs={12}>
          <Box flexDirection={'column'} alignItems={'flex-start'}>
            <TextField
              id="rollover-apr"
              sx={{ width: '300px' }}
              variant="outlined"
              placeholder="Enter Finalzied APR"
              value={apr}
              type={'number'}
              onChange={handleOnChangeAPR}
              error={!!errorAPR}
              helperText={errorAPR}
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Button
            disabled={isSubmitted}
            sx={sx}
            onClick={() => {
              finalizeRolloverAction()
            }}
            variant={'contained'}
          >
            <Typography variant={'buttonBold'}>Start Accrual</Typography>
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}
